import type { ReactiveController } from 'lit'
import type { ShortcutMap } from '../common/controllers/ShortcutController.js'
import type CommandMenu from './CommandMenu.js'
import { ShortcutController } from '../common/controllers/ShortcutController.js'
import { getFocusedElement, isEditableElement } from '../common/focus.js'

interface KeyboardActions {
  trigger: () => void
  goBack: () => void
  start: () => void
  end: () => void
  next: () => void
  previous: () => void
  toggleOpen: () => void
}

function preventDefault(fn: (e: Event) => void) {
  return (e: Event) => {
    e.preventDefault()
    fn(e)
  }
}

/**
 * Turns a command's `shortcut` into the binding handed to tinykeys.
 *
 * A single-character shortcut (e.g. `/`) should fire whenever the user produces
 * that character, no matter which physical keys the layout requires. On many
 * non-US layouts `/` is a shifted key — `Shift+7` on German and Nordic,
 * `Shift+:` on French AZERTY — so a bare `/` binding, which declares no
 * modifiers, is rejected by tinykeys the instant Shift is held, and the
 * shortcut silently fails for those users. Registering it with tinykeys'
 * optional-`Shift` bracket syntax lets it match with or without Shift while
 * still matching on the produced character: on a US layout `Shift+/` produces
 * `?`, a different key, so it never triggers the `/` binding. The command's
 * `shortcut` field is left untouched, so the visible chip stays a single `/`.
 */
function toKeybinding(shortcut: string): string {
  return shortcut.length === 1 ? `[Shift]+${shortcut}` : shortcut
}

export class KeyboardController {
  private globalShortcuts: ShortcutController
  private navigationShortcuts: ShortcutController
  private commandShortcuts: ShortcutController

  constructor(
    private host: CommandMenu,
    actions: KeyboardActions,
  ) {
    host.addController(this as ReactiveController)

    this.commandShortcuts = new ShortcutController(host)
    this.globalShortcuts = new ShortcutController(host, { '$mod+k': preventDefault(actions.toggleOpen) })
    this.navigationShortcuts = new ShortcutController(
      host,
      {
        Enter: preventDefault(actions.trigger),
        Backspace: actions.goBack, // we don't want to prevent default, since that would stop backspace deleting chars
        End: preventDefault(actions.end),
        Home: preventDefault(actions.start),
        ArrowDown: preventDefault(actions.next),
        ArrowUp: preventDefault(actions.previous),
      },
      host,
    )
  }

  registerCommandShortcuts() {
    const shortcuts: ShortcutMap = {}

    for (const command of this.host.commands) {
      if (command.shortcut) {
        shortcuts[toKeybinding(command.shortcut)] = (event: Event) => {
          // Command shortcuts bind to `window`, so an Alt+letter combo (e.g.
          // Alt+L → ¬, Alt+S → ß on some layouts) can fire the command instead
          // of typing into the focused field. Commands opting into
          // `ignoreWhileTyping` are skipped while an editable element is focused.
          // `getFocusedElement` resolves through shadow roots so Nord's own form
          // hosts (e.g. `nord-input`) are matched via their inner native field.
          if (command.ignoreWhileTyping && isEditableElement(getFocusedElement(document))) {
            return
          }

          // The handler always fires. Only override the browser default when the
          // command explicitly opts in via `forceShortcut`, acknowledging it is
          // reclaiming a combo the browser may reserve (e.g. Alt+D = address bar).
          if (command.forceShortcut) {
            event.preventDefault()
          }
          command.handler?.(this.host)
        }
      }
    }

    this.commandShortcuts.bind(shortcuts)
  }
}
